import { useState, useEffect } from 'react';
import { CreditCard, Edit, X, Save, Search, Upload, Download, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSignedUrl } from '../../hooks/useSignedUrl';
import { openStoredFile } from '../../utils/fileUrls';
import { downloadDocument } from '../../utils/downloadDocument';

export function DriverCdlTwicView() {
  const toast = useToast();
  const { profile } = useAuth();
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDriver, setEditingDriver] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    cdl_number: '',
    cdl_expiration: '',
    cdl_image_url: '',
    twic_image_url: ''
  });
  const [uploadingCdl, setUploadingCdl] = useState(false);
  const [uploadingTwic, setUploadingTwic] = useState(false);
  const [previewModal, setPreviewModal] = useState<{ url: string; type: 'cdl' | 'twic'; driverName: string } | null>(null);
  const { url: previewSignedUrl, loading: previewLoading, error: previewError } = useSignedUrl(previewModal?.url);

  // Check if user can edit
  const canEdit = profile?.role === 'Safety' || profile?.role === 'Safety Manager' || 
                  profile?.role === 'Superadmin' || profile?.role === 'superadmin' || 
                  profile?.role === 'Admin' || profile?.role === 'admin';

  useEffect(() => {
    loadDrivers();
  }, []);

  async function loadDrivers() {
    try {
      setLoading(true);
      
      // Load all drivers from all 3 companies
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, cdl_number, cdl_expiration, cdl_image_url, twic_image_url, driver_status, company_id, companies(name, code)')
        .order('last_name');

      if (error) throw error;
      
      // Filter to show all non-terminated drivers (active, inactive/new, vacation)
      const nonTerminatedDrivers = (data || []).filter(d => 
        d.driver_status !== 'terminated'
      );
      
      console.log('CDL/TWIC: Total drivers:', data?.length || 0);
      console.log('CDL/TWIC: Non-terminated drivers:', nonTerminatedDrivers.length);
      console.log('CDL/TWIC: Drivers by company:', nonTerminatedDrivers.reduce((acc: any, d: any) => {
        const company = d.companies?.code || 'UNASSIGNED';
        acc[company] = (acc[company] || 0) + 1;
        return acc;
      }, {}));
      
      setDrivers(nonTerminatedDrivers);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }

  const handleEdit = (driver: any) => {
    setEditingDriver(driver.id);
    setEditForm({
      cdl_number: driver.cdl_number || '',
      cdl_expiration: driver.cdl_expiration || '',
      cdl_image_url: driver.cdl_image_url || '',
      twic_image_url: driver.twic_image_url || ''
    });
  };

  const handleCancel = () => {
    setEditingDriver(null);
    setEditForm({
      cdl_number: '',
      cdl_expiration: '',
      cdl_image_url: '',
      twic_image_url: ''
    });
  };

  const handleImageUpload = async (driverId: string, file: File, type: 'cdl' | 'twic') => {
    try {
      if (type === 'cdl') setUploadingCdl(true);
      else setUploadingTwic(true);

      const driver = drivers.find(d => d.id === driverId);
      const fileExt = file.name.split('.').pop();
      const fileName = `${driver.first_name}_${driver.last_name}_${type}_${Date.now()}.${fileExt}`;
      const filePath = `driver-documents/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-documents')
        .getPublicUrl(filePath);

      const updateField = type === 'cdl' ? 'cdl_image_url' : 'twic_image_url';
      const { error: updateError } = await supabase
        .from('drivers')
        .update({ [updateField]: publicUrl })
        .eq('id', driverId);

      if (updateError) throw updateError;

      toast.success(`${type.toUpperCase()} image uploaded successfully`);
      loadDrivers();
    } catch (error) {
      console.error(`Error uploading ${type} image:`, error);
      toast.error(`Failed to upload ${type.toUpperCase()} image`);
    } finally {
      if (type === 'cdl') setUploadingCdl(false);
      else setUploadingTwic(false);
    }
  };

  const handleRemoveImage = async (driverId: string, type: 'cdl' | 'twic') => {
    if (!confirm(`Are you sure you want to remove this ${type.toUpperCase()} image?`)) return;

    try {
      const updateData = type === 'cdl' 
        ? { cdl_image_url: null }
        : { twic_image_url: null };

      console.log('Removing image:', { driverId, type, updateData });

      const { data, error, status, statusText } = await supabase
        .from('drivers')
        .update(updateData)
        .eq('id', driverId)
        .select();

      console.log('Update response:', { data, error, status, statusText });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.error('No data returned - possible RLS policy issue');
        toast.error('Update failed - permission denied');
        return;
      }

      console.log('Image removed successfully from database, updated driver:', data[0]);
      
      // Update local state immediately
      setDrivers(prevDrivers => 
        prevDrivers.map(driver => 
          driver.id === driverId 
            ? { ...driver, ...updateData }
            : driver
        )
      );

      toast.success(`${type.toUpperCase()} image removed successfully`);
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Failed to remove image');
    }
  };

  const handleSave = async (driverId: string) => {
    try {
      const { error } = await supabase
        .from('drivers')
        .update({
          cdl_number: editForm.cdl_number || null,
          cdl_expiration: editForm.cdl_expiration || null,
          cdl_image_url: editForm.cdl_image_url || null,
          twic_image_url: editForm.twic_image_url || null
        })
        .eq('id', driverId);

      if (error) throw error;

      toast.success('Driver information updated successfully');
      setEditingDriver(null);
      loadDrivers();
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error('Failed to update driver information');
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const query = searchQuery.toLowerCase();
    const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return (
      fullName.includes(query) ||
      driver.cdl_number?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading drivers...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Driver CDL & TWIC
              <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                {filteredDrivers.length} {filteredDrivers.length === 1 ? 'Driver' : 'Drivers'}
              </span>
            </h1>
            <p className="text-slate-600 mt-1">Manage driver CDL and TWIC card information</p>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by driver name, CDL, or TWIC..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Driver</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">CDL Number</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">CDL Expiration</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">CDL Image</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">TWIC Image</th>
                {canEdit && (
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-24">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDrivers.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 6 : 5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="w-12 h-12 text-slate-300" />
                      <p className="text-slate-500 font-medium">
                        {searchQuery ? 'No drivers match your search' : 'No active drivers found'}
                      </p>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredDrivers.map((driver) => {
                  const isEditing = editingDriver === driver.id;
                  
                  return (
                    <tr key={driver.id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-slate-900">
                          {driver.first_name} {driver.last_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.cdl_number}
                            onChange={(e) => setEditForm({ ...editForm, cdl_number: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="CDL Number"
                          />
                        ) : (
                          <span className="text-sm text-slate-600 font-mono">{driver.cdl_number || '-'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editForm.cdl_expiration}
                            onChange={(e) => setEditForm({ ...editForm, cdl_expiration: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-slate-600">
                            {driver.cdl_expiration ? new Date(driver.cdl_expiration).toLocaleDateString() : '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {driver.cdl_image_url ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setPreviewModal({ 
                                  url: driver.cdl_image_url, 
                                  type: 'cdl', 
                                  driverName: `${driver.first_name} ${driver.last_name}` 
                                })}
                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-1"
                              >
                                <FileText className="w-3 h-3" />
                                Preview
                              </button>
                              {canEdit && (
                                <div className="flex items-center gap-1">
                                  <label className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-all cursor-pointer" title="Replace">
                                    <Upload className="w-3.5 h-3.5" />
                                    <input
                                      type="file"
                                      accept="image/*,application/pdf"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleImageUpload(driver.id, file, 'cdl');
                                      }}
                                    />
                                  </label>
                                  <button
                                    onClick={() => handleRemoveImage(driver.id, 'cdl')}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-all"
                                    title="Remove"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : canEdit ? (
                            <label className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all cursor-pointer flex items-center gap-1">
                              <Upload className="w-3 h-3" />
                              Upload
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleImageUpload(driver.id, file, 'cdl');
                                }}
                              />
                            </label>
                          ) : (
                            <span className="text-xs text-slate-400">No image</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {driver.twic_image_url ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setPreviewModal({ 
                                  url: driver.twic_image_url, 
                                  type: 'twic', 
                                  driverName: `${driver.first_name} ${driver.last_name}` 
                                })}
                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-1"
                              >
                                <FileText className="w-3 h-3" />
                                Preview
                              </button>
                              {canEdit && (
                                <div className="flex items-center gap-1">
                                  <label className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-all cursor-pointer" title="Replace">
                                    <Upload className="w-3.5 h-3.5" />
                                    <input
                                      type="file"
                                      accept="image/*,application/pdf"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleImageUpload(driver.id, file, 'twic');
                                      }}
                                    />
                                  </label>
                                  <button
                                    onClick={() => handleRemoveImage(driver.id, 'twic')}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-all"
                                    title="Remove"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : canEdit ? (
                            <label className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all cursor-pointer flex items-center gap-1">
                              <Upload className="w-3 h-3" />
                              Upload
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleImageUpload(driver.id, file, 'twic');
                                }}
                              />
                            </label>
                          ) : (
                            <span className="text-xs text-slate-400">No image</span>
                          )}
                        </div>
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={() => handleSave(driver.id)}
                                  className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all hover:shadow-sm"
                                  title="Save"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleCancel}
                                  className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all hover:shadow-sm"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEdit(driver)}
                                  className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all hover:shadow-sm"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                {(driver.cdl_image_url || driver.twic_image_url) && (
                                  <button
                                    onClick={async () => {
                                      const urls = [];
                                      if (driver.cdl_image_url) urls.push({ url: driver.cdl_image_url, name: `${driver.first_name}_${driver.last_name}_CDL` });
                                      if (driver.twic_image_url) urls.push({ url: driver.twic_image_url, name: `${driver.first_name}_${driver.last_name}_TWIC` });
                                      let failed = 0;
                                      for (const { url, name } of urls) {
                                        try {
                                          await downloadDocument(url, name);
                                        } catch {
                                          failed++;
                                        }
                                      }
                                      if (failed === 0) toast.success(urls.length === 1 ? 'Image downloaded' : 'Images downloaded');
                                      else toast.error(`${failed} of ${urls.length} image(s) could not be downloaded`);
                                    }}
                                    className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all hover:shadow-sm"
                                    title="Download Images"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setPreviewModal(null)}>
          <div className="relative max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {previewModal.driverName} - {previewModal.type.toUpperCase()}
              </h3>
              <button
                onClick={() => setPreviewModal(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {previewLoading ? (
                <div className="w-full h-[40vh] flex items-center justify-center text-sm text-slate-500" aria-busy="true">
                  Loading document...
                </div>
              ) : previewError || !previewSignedUrl ? (
                <div className="w-full h-[40vh] flex items-center justify-center text-sm text-red-600">
                  This document could not be loaded.
                </div>
              ) : previewModal.url.split('?')[0].toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewSignedUrl}
                  className="w-full h-[70vh] border-0 rounded"
                  title={`${previewModal.type.toUpperCase()} Document`}
                />
              ) : (
                <img
                  src={previewSignedUrl}
                  alt={previewModal.type.toUpperCase()}
                  className="max-w-full max-h-[70vh] object-contain mx-auto rounded"
                />
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await downloadDocument(previewModal.url, `${previewModal.driverName.replace(/\s+/g, '_')}_${previewModal.type.toUpperCase()}`);
                  } catch {
                    toast.error('Failed to download document');
                  }
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                type="button"
                onClick={async () => {
                  const opened = await openStoredFile(previewModal.url);
                  if (!opened) toast.error('Document could not be opened');
                }}
                className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Open in New Tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
